import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { ListChecks, Loader2, Plus, X as XIcon, Trash2, Upload, RefreshCw, Phone, CheckCircle2, Users, FileText, Eye } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";

const emptyDraft = () => ({
  name: "", description: "", source_type: "csv",
  assignment_mode: "pool", team_id: "", active: true,
});

export default function AdminCallLists() {
  const { user: currentUser } = useAuth();
  const [lists, setLists] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importTarget, setImportTarget] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [entriesView, setEntriesView] = useState(null);
  const [entries, setEntries] = useState([]);
  const [reviewRequired, setReviewRequired] = useState(true);
  const fileInputRef = useRef(null);

  const isDirector = currentUser?.role === "telesales_director";

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: l }, { data: t }] = await Promise.all([
        api.get("/telesales/lists?show_inactive=false"),
        api.get("/admin/sales-teams"),
      ]);
      setLists(l || []);
      setTeams(t || []);
      if (!isDirector) {
        try {
          const r = await api.get("/telesales/admin/review-setting");
          setReviewRequired(!!r.data?.telesales_review_required);
        } catch (_) { /* settings optional */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const save = async () => {
    if (!editor.name) return toast.error("Name ist Pflicht");
    if (!editor.team_id) return toast.error("Team ist Pflicht");
    setSaving(true);
    try {
      const payload = { ...editor };
      delete payload.isNew; delete payload.id;
      delete payload.created_at; delete payload.updated_at;
      delete payload.counts;
      if (editor.isNew) await api.post("/telesales/lists", payload);
      else await api.patch(`/telesales/lists/${editor.id}`, payload);
      toast.success(editor.isNew ? "Liste angelegt" : "Gespeichert");
      setEditor(null); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Liste „${l.name}" wirklich löschen? (Adressen bleiben in der DB)`)) return;
    try {
      await api.delete(`/telesales/lists/${l.id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const submitCsv = async (file) => {
    if (!importTarget || !file) return;
    setImporting(true); setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/telesales/lists/${importTarget.id}/import-csv`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.imported} Adressen importiert (${data.skipped} übersprungen)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
    } finally { setImporting(false); }
  };

  const viewEntries = async (l) => {
    setEntriesView(l); setEntries([]);
    try {
      const { data } = await api.get(`/telesales/lists/${l.id}/entries?limit=200`);
      setEntries(data || []);
    } catch (e) { toast.error("Adressen laden fehlgeschlagen"); }
  };

  const toggleReview = async () => {
    const next = !reviewRequired;
    setReviewRequired(next);
    try {
      await api.put("/telesales/admin/review-setting", { telesales_review_required: next });
      toast.success(next
        ? "Direktor-Freigabe AKTIV (Auto-Release aus)"
        : "Auto-Release AKTIV — Leads gehen direkt an Sales");
    } catch (e) {
      setReviewRequired(!next);
      toast.error("Speichern fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-call-lists">
      <AppHeader title="Telesales-Call-Listen" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
              <ListChecks className="text-[#00E575]" size={22} /> Call-Listen & Adressbestand
            </h1>
            <p className="text-[12px] text-zinc-300 font-medium">Importiere Adressen, weise Listen an Telesales-Teams zu.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="call-lists-refresh" className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium">
              <RefreshCw size={14} /> Neu laden
            </button>
            <button
              onClick={() => setEditor({ isNew: true, ...emptyDraft() })}
              data-testid="call-list-new"
              className="h-9 px-3 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5"
            >
              <Plus size={14} /> Neue Liste
            </button>
          </div>
        </div>

        {/* Setting: review required (Auto-Release = inverse) */}
        {!isDirector && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="review-setting-card">
            <div>
              <div className="text-[13px] font-bold">
                {reviewRequired
                  ? "Direktor-Freigabe vor Sales-Routing (Auto-Release AUS)"
                  : "Auto-Release AKTIV — Leads gehen direkt in Sales-Routing"}
              </div>
              <div className="text-[12px] text-zinc-400">
                {reviewRequired
                  ? "Alle Telesales-Leads landen erst beim Direktor. Umschalten = Auto-Release ein (Direktor-Review wird übersprungen)."
                  : "Direktor-Review wird übersprungen. Neue Leads werden sofort an einen Sales-Rep zugewiesen. Umschalten = Direktor-Freigabe wieder aktiv."}
              </div>
            </div>
            <button
              onClick={toggleReview}
              data-testid="review-toggle"
              title={reviewRequired ? "Auf Auto-Release umschalten" : "Direktor-Freigabe wieder aktivieren"}
              className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${reviewRequired ? "bg-[#00E575]" : "bg-amber-500"}`}
            >
              <span className={`absolute left-0 top-0.5 h-6 w-6 rounded-full bg-white transition-transform ${reviewRequired ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
        )}

        {/* Listen */}
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-zinc-500" size={28} /></div>
        ) : lists.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-12 text-center" data-testid="call-lists-empty">
            <ListChecks className="mx-auto text-zinc-600 mb-3" size={48} />
            <h3 className="font-bold text-lg mb-1">Noch keine Listen</h3>
            <p className="text-zinc-300 text-sm font-medium">Lege deine erste Call-Liste an und importiere eine CSV.</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="call-lists-grid">
            {lists.map((l) => {
              const teamName = teams.find((t) => t.id === l.team_id)?.name || "—";
              const c = l.counts || { open: 0, in_call: 0, called: 0, total: 0 };
              return (
                <div
                  key={l.id}
                  data-testid={`call-list-${l.id}`}
                  className="bg-[#18181B] border border-zinc-800 hover:border-zinc-600 rounded-2xl p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-[15px]">{l.name}</h3>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${l.assignment_mode === "pool" ? "bg-zinc-800 text-zinc-400" : "bg-purple-500/15 text-purple-300"}`}>
                          {l.assignment_mode === "pool" ? "Pool" : "Direkt"}
                        </span>
                      </div>
                      {l.description && <p className="text-[12px] text-zinc-400 mb-2 font-medium">{l.description}</p>}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-zinc-400">
                        <span className="inline-flex items-center gap-1"><Users size={11} /> {teamName}</span>
                        <span className="inline-flex items-center gap-1"><FileText size={11} /> {l.source_type}</span>
                      </div>
                      <div className="flex gap-3 mt-3 text-[12px]">
                        <Counter color="#94a3b8" label="Gesamt" value={c.total} />
                        <Counter color="#00E575" label="Offen" value={c.open} />
                        <Counter color="#60a5fa" label="In Bearbeitung" value={c.in_call} />
                        <Counter color="#f59e0b" label="Erledigt" value={c.called} />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button
                        onClick={() => { setImportTarget(l); setImportResult(null); }}
                        data-testid={`call-list-${l.id}-import`}
                        className="h-8 px-3 rounded-lg bg-[#00E575]/10 hover:bg-[#00E575]/20 text-[#00E575] text-[12px] font-bold inline-flex items-center gap-1.5"
                      >
                        <Upload size={12} /> CSV-Import
                      </button>
                      <button
                        onClick={() => viewEntries(l)}
                        data-testid={`call-list-${l.id}-view`}
                        className="h-8 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px] inline-flex items-center gap-1.5"
                      >
                        <Eye size={12} /> Adressen
                      </button>
                      <button
                        onClick={() => setEditor({ isNew: false, ...l })}
                        data-testid={`call-list-${l.id}-edit`}
                        className="h-8 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px]"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => remove(l)}
                        data-testid={`call-list-${l.id}-delete`}
                        className="h-8 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[12px] inline-flex items-center gap-1.5"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Editor modal */}
        {editor && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-3" data-testid="call-list-editor">
            <div className="w-full max-w-md bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                <h3 className="font-bold">{editor.isNew ? "Neue Liste" : "Liste bearbeiten"}</h3>
                <button onClick={() => setEditor(null)} className="p-1 hover:bg-zinc-800 rounded">
                  <XIcon size={16} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <label className={labelCls}>Name *</label>
                  <input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="call-list-edit-name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Beschreibung</label>
                  <input value={editor.description || ""} onChange={(e) => setEditor({ ...editor, description: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Telesales-Team *</label>
                  <select
                    value={editor.team_id}
                    onChange={(e) => setEditor({ ...editor, team_id: e.target.value })}
                    data-testid="call-list-edit-team"
                    className={inputCls}
                  >
                    <option value="">— Team auswählen —</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.type})</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Zuweisungs-Modus</label>
                    <select
                      value={editor.assignment_mode}
                      onChange={(e) => setEditor({ ...editor, assignment_mode: e.target.value })}
                      data-testid="call-list-edit-mode"
                      className={inputCls}
                    >
                      <option value="pool">Pool (First-Come)</option>
                      <option value="direct">Direkt (pro Adresse)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Quelle</label>
                    <select
                      value={editor.source_type}
                      onChange={(e) => setEditor({ ...editor, source_type: e.target.value })}
                      className={inputCls}
                    >
                      <option value="csv">CSV-Import</option>
                      <option value="external_api">Externe API</option>
                      <option value="manual">Manuell</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="call-list-save"
                  className="w-full h-11 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:bg-zinc-800 disabled:text-zinc-500"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : "Speichern"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import modal */}
        {importTarget && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-3" data-testid="csv-import-modal">
            <div className="w-full max-w-md bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                <h3 className="font-bold">CSV-Import — {importTarget.name}</h3>
                <button onClick={() => { setImportTarget(null); setImportResult(null); }} className="p-1 hover:bg-zinc-800 rounded">
                  <XIcon size={16} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-[12px] text-zinc-300 font-medium">
                  Erwartete Spalten (Header-Zeile):<br />
                  <code className="text-zinc-300 text-[12px]">salutation, first_name, last_name, phone, email, street, zip, city, note</code><br />
                  Pflicht: <code>first_name</code> + <code>last_name</code> + (<code>phone</code> ODER <code>email</code>).
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => e.target.files?.[0] && submitCsv(e.target.files[0])}
                  data-testid="csv-import-file"
                  className="block w-full text-[12px] text-zinc-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#00E575] file:text-[#09090B] file:font-bold file:cursor-pointer hover:file:bg-[#00C964]"
                />
                {importing && (
                  <div className="flex items-center justify-center py-3 text-zinc-400">
                    <Loader2 className="animate-spin mr-2" size={16} /> Import läuft …
                  </div>
                )}
                {importResult && (
                  <div className="p-3 rounded-lg bg-[#00E575]/5 border border-[#00E575]/20" data-testid="csv-import-result">
                    <div className="text-[12px] text-[#00E575] font-bold">✓ {importResult.imported} importiert, {importResult.skipped} übersprungen</div>
                    {importResult.skipped_details?.length > 0 && (
                      <ul className="mt-2 text-[10.5px] text-zinc-400 space-y-0.5 max-h-32 overflow-y-auto">
                        {importResult.skipped_details.slice(0, 10).map((s, i) => (
                          <li key={i}>Zeile {s.line}: {s.reason}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Entries view */}
        {entriesView && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-3" data-testid="entries-modal">
            <div className="w-full max-w-3xl bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 sticky top-0 bg-[#18181B]">
                <h3 className="font-bold">Adressen — {entriesView.name}</h3>
                <button onClick={() => setEntriesView(null)} className="p-1 hover:bg-zinc-800 rounded">
                  <XIcon size={16} />
                </button>
              </div>
              <div className="p-3">
                {entries.length === 0 ? (
                  <div className="text-center py-8 text-zinc-400 text-sm font-medium">Keine Einträge</div>
                ) : (
                  <table className="w-full text-[12px] font-medium">
                    <thead className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold">
                      <tr className="border-b border-zinc-800">
                        <th className="text-left py-2 px-2">Name</th>
                        <th className="text-left py-2 px-2">Telefon</th>
                        <th className="text-left py-2 px-2">Ort</th>
                        <th className="text-left py-2 px-2">Status</th>
                        <th className="text-left py-2 px-2">Outcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e) => (
                        <tr key={e.id} className="border-b border-zinc-900">
                          <td className="py-1.5 px-2">{e.first_name} {e.last_name}</td>
                          <td className="py-1.5 px-2 font-mono text-[12px] text-zinc-400">{e.phone || "—"}</td>
                          <td className="py-1.5 px-2 text-zinc-400">{e.zip} {e.city}</td>
                          <td className="py-1.5 px-2">
                            <span className={`px-1.5 py-0.5 rounded text-[12px] ${
                              e.status === "open" ? "bg-zinc-800 text-zinc-300"
                              : e.status === "in_call" ? "bg-blue-500/15 text-blue-300"
                              : "bg-[#00E575]/15 text-[#00E575]"
                            }`}>{e.status}</span>
                          </td>
                          <td className="py-1.5 px-2 text-zinc-400">{e.outcome || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const Counter = ({ color, label, value }) => (
  <div className="flex items-baseline gap-1">
    <span className="font-bold font-mono" style={{ color }}>{value}</span>
    <span className="text-zinc-300 text-[12px] uppercase tracking-widest">{label}</span>
  </div>
);
